import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, ElementRef, OnDestroy, OnChanges, SimpleChanges, HostListener, Optional } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { AutoResizeTextareaDirective } from '../../../directives/textarea/auto-resize-textarea.directive';
import { ChatbotService } from '../../../services/chatbot-services/chatbot.service';
import { environment } from 'src/environments/environment';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { PopupMessageEvent, GetChatSessionsInfoEvent, LoadMoreSessionsEvent, TabSelectionEvent } from '../../models/chat-sessions.models';
import { AgentAccessControlService } from '../../agents/services/agent-access-control/agent-access-control.service';
import { AgentConfig, GetAgentResponseHistoryRequest } from '../../agents/models';
import { AgentFeatureService } from '../../agents/services/agent-feature/agent-feature.service';
import { AgentOrchestratorService } from '../../agents/services/agent-orchestrator/agent-orchestrator.service';
import { normalizeRetrievalMode } from '../../../shared/utils/retrieval-mode.util';
import { GetAgentResponseHistoryService } from '../../agents/services/agent-response-history';
import { ConfigurationService } from 'src/services/configuration/configuration.service';

@Component({
  selector: 'app-chat-sessions',
  templateUrl: './chat-sessions.component.html',
  styleUrls: ['./chat-sessions.component.scss']
})
export class ChatSessionsComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('dropdown') dropdownElement!: ElementRef;
  @ViewChild(AutoResizeTextareaDirective) autoResizeDirective!: AutoResizeTextareaDirective;
  @ViewChild('sessionHistory') sessionHistoryComponent: any;
  @Input() chatSessionList: any[] = [];
  @Input() vectorDbDatasets: any[] = [];
  @Input() isResponseScroll: boolean = false;
  @Input() txtChat: string = '';
  @Input() IsLikeSelected: boolean = false;
  @Input() selectedFilter: string = '30 Days';
  @Input() isLoadingsession: boolean = false;
  @Input() datasetLoadingStates: { [datasetName: string]: boolean } = {};
  @Input() IsResponseStreaming: boolean = false;
  @Input() ChatQueriesList: any[] = [];
  @Input() currentSessionId: any[] = [];
  @Input() loaderShow: boolean = false;
  @Input() isProcessing: boolean = false;
  @Input() hasActiveStreamingSubscription: boolean = false;
  @Input() moduleThreadsCounts: { [moduleName: string]: number } = {};
  @Input() selectedTabInput: string = 'All';
  @Input() datasetApiTriggered: { [datasetName: string]: boolean } = {};
  @Input() privilegesData?: { cookiesArray: string[]; agentPrivileges: string[] };


  @Output() closeHistory = new EventEmitter<void>();
  @Output() resetChatState = new EventEmitter<void>();
  @Output() deleteChat = new EventEmitter<number>();
  @Output() getChatHistory = new EventEmitter<string[]>();
  @Output() showPopupMessage = new EventEmitter<PopupMessageEvent>();
  @Output() getChatSessionsInfo = new EventEmitter<GetChatSessionsInfoEvent>();
  @Output() ClickNewChat = new EventEmitter<boolean>();
  @Output() loadMoreSessions = new EventEmitter<LoadMoreSessionsEvent>();
  @Output() tabSelected = new EventEmitter<TabSelectionEvent>();
  @Output() updateFilter = new EventEmitter<string>();
  @Output() universalLoading = new EventEmitter<boolean>();
  @Output() chatSessionListChange = new EventEmitter<any[]>();
  @Output() showDeleteSuccessMessage = new EventEmitter<void>();
  @Output() showEditSuccessMessage = new EventEmitter<void>();
  @Output() showShareSuccessMessage = new EventEmitter<void>();
  @Output() stopStreamingFunc = new EventEmitter<void>();
  @Output() stopSpeakingFunc = new EventEmitter<void>();
  @Output() resizeDropdownWidth = new EventEmitter<void>();
  @Output() scrollToBottomAfterSession = new EventEmitter<void>();
  @Output() datasetToggled = new EventEmitter<{datasetName: string, isExpanded: boolean}>();
  @Output() agentSelectedFromSidebar = new EventEmitter<string>(); // Emits agent ID as string

  selectedTab: string = '';
  tabList: string[] = [];
  UserID: string | undefined;
  tenantID: string | undefined;
  DomainID: string | undefined;
  expandedDatasets: { [key: string]: boolean } = {};
  showFilterDropdown = false;
  selectedItemForAction: any = null;
  availableAgents: AgentConfig[] = [];
  currentAgentId: string = 'module';
  isAgentMode: boolean = false; // True when agent is selected, false for chatbot
  // Session switch confirmation popup state
  showSessionSwitchConfirmationPopup: boolean = false;
  pendingSessionSwitch: any = null;

  // Filter switch confirmation popup state
  showFilterSwitchConfirmationPopup: boolean = false;
  pendingFilterSwitch: string = '';

  public scrollSubscriptions: { [key: string]: Subscription } = {};
  private scrollTimeouts: { [key: string]: any } = {};

  private readonly ChatSessionsError = (error: any)=>
    `[ChatSessions] ${JSON.stringify(error)}`;

  private agentIdSubscription: Subscription | null = null;
  private readonly subscriptions: Subscription[] = [];

  // Agent chat history properties
  private currentThreadIdForHistory: string = '';
  private historyCurrentPage: number = 1;
  private historyTotalPages: number = 1;
  private isLoadingMoreHistory: boolean = false;

  constructor(
    public chatbotService: ChatbotService,
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    public loggerService: LoggerService,
    private readonly agentAccessControlService: AgentAccessControlService,
    private readonly agentFeatureService: AgentFeatureService,
    private readonly getAgentResponseHistoryService: GetAgentResponseHistoryService,
    private readonly configurationService: ConfigurationService,
    @Optional() private agentOrchestrator?: AgentOrchestratorService

  ) { }

  ngOnInit(): void {
    try{
      this.UserID = environment.LoginUserID;
      this.tenantID = environment.TenantId;
      this.DomainID = environment.GlobalInfo["DomainID"];
      this.initializeTabList();
      this.initializeExpandedDatasets();
      this.resizeDropdownWidth.emit();
      this.loadAvailableAgents();

      if (this.agentOrchestrator) {
        this.agentIdSubscription = this.agentOrchestrator.currentAgentId$.subscribe((agentId: string) => {
          this.currentAgentId = agentId;
          this.isAgentMode = agentId !== 'module';
        });
        
        // Subscribe to new session creation events
        const sessionCreationSub = this.agentOrchestrator.newSessionCreated$.subscribe(({ threadName, threadId }: { threadName: string, threadId: string }) => {
          this.addAgentSession(threadName, threadId);
        });
        this.subscriptions.push(sessionCreationSub);

        // Register lazy load callback for agent chat history
        this.agentOrchestrator.registerLazyLoadCallback(() => {
          return this.onLoadMoreAgentChatHistory();
        });
      }
    } 
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error),"ngOnInit")
    }
  }

  public initializeTabList(): void {
    try{
      // Always include 'All' tab regardless of dataset count
      this.tabList = ['All', ...this.vectorDbDatasets.map(dataset => dataset.name)];
      this.selectedTab = 'All';
    }
    catch(error: any){
      this.loggerService.logError(this.ChatSessionsError(error), "initializeTabList");
    }
  }

  public initializeExpandedDatasets(): void {
    try{
      this.vectorDbDatasets.forEach(dataset => {
        this.expandedDatasets[dataset.name] = false;
      });
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error),"initializeExpandedDatasets")
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    try{
      if (changes['vectorDbDatasets'] || changes['selectedTab']) {
        this.initializeTabList();
        this.initializeExpandedDatasets();
        this.updateScrollSubscriptions();
      }
      this.resizeDropdownWidth.emit();
    }
    catch(error: any){
      this.loggerService.logError(this.ChatSessionsError(error), "ngOnChanges");
    }
  }

  ngAfterViewInit(): void {
    this.updateScrollSubscriptions();
  }

  public updateScrollSubscriptions(): void {
    try{
      Object.values(this.scrollSubscriptions).forEach(sub => sub.unsubscribe());
      this.scrollSubscriptions = {};

      if (this.selectedTab === 'All') {
        this.vectorDbDatasets.forEach(dataset => {
          if (this.expandedDatasets[dataset.name]) {
            const folderElement = document.querySelector(`.scrollable-folder[data-dataset="${dataset.name}"]`) as HTMLElement;
            if (folderElement) {
              this.scrollSubscriptions[dataset.name] = fromEvent(folderElement, 'scroll')
                .pipe(debounceTime(200))
                .subscribe((event: Event) => this.onFolderScroll(event, dataset.name));
            }
          }
        });
      } else {
        const tabElement = document.querySelector('.scrollable-tab') as HTMLElement;
        if (tabElement) {
          this.scrollSubscriptions[this.selectedTab] = fromEvent(tabElement, 'scroll')
            .pipe(debounceTime(200))
            .subscribe((event: Event) => this.onTabScroll(event, this.selectedTab));
        }
      }
    }
    catch(error: any){
      this.loggerService.logError(this.ChatSessionsError(error), "updateScrollSubscriptions");
    }

  }

  onFolderScroll(event: Event, datasetName: string): void {
    try{
      const element = event.target as HTMLElement;
      if (!element) return;
      
      // Clear existing timeout for this dataset to debounce scroll events
      if (this.scrollTimeouts[datasetName]) {
        clearTimeout(this.scrollTimeouts[datasetName]);
      }
      
      this.scrollTimeouts[datasetName] = setTimeout(() => {
        // Use dynamic threshold based on element height (minimum 20px, maximum 100px)
        const threshold = Math.min(Math.max(element.clientHeight * 0.05, 20), 100);
        const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + threshold;
        
        if (atBottom) {
          this.loadMoreSessions.emit({ datasetName });
        }
      }, 100); // 100ms debounce
    }
    catch(error: any){
      this.loggerService.logError(this.ChatSessionsError(error), "onFolderScroll");
    }
  }

  onTabScroll(event: Event, tabName: string): void {
    try{
      const element = event.target as HTMLElement;
      if (!element) return;
      
      // Clear existing timeout for this tab to debounce scroll events
      if (this.scrollTimeouts[tabName]) {
        clearTimeout(this.scrollTimeouts[tabName]);
      }
      
      this.scrollTimeouts[tabName] = setTimeout(() => {
        // Use dynamic threshold based on element height (minimum 20px, maximum 100px)
        const threshold = Math.min(Math.max(element.clientHeight * 0.05, 20), 100);
        const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + threshold;
        
        if (atBottom) {
          this.loadMoreSessions.emit({ datasetName: tabName });
        }
      }, 100); // 100ms debounce
    }
    catch(error: any){
      this.loggerService.logError(this.ChatSessionsError(error), "onTabScroll");
    }
  }

  ngOnDestroy(): void {
    try{
      Object.values(this.scrollSubscriptions).forEach(sub => sub.unsubscribe());
      // Clear all scroll timeouts
      Object.values(this.scrollTimeouts).forEach(timeout => {
        if (timeout) {
          clearTimeout(timeout);
        }
      });
      if (this.agentIdSubscription) {
        this.agentIdSubscription.unsubscribe();
      }
      // Clean up all subscriptions
      this.subscriptions.forEach(sub => sub.unsubscribe());
    }
    catch(error: any){
      this.loggerService.logError(this.ChatSessionsError(error), "ngOnDestroy");
    }
  }

  onCloseHistory() {
    try {
      this.closeHistory.emit();
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "onCloseHistory");
    }
  }

  onSelectSession(session: any): void {
    try {
      // Check if the clicked session is already the currently selected session
      const clickedSessionIndex = this.chatSessionList.findIndex((item) => item.session_id === session.session_id);
      if (clickedSessionIndex === -1) return;
      
      const clickedSession = this.chatSessionList[clickedSessionIndex];
      
      // If the same session is already selected, just close dropdowns and return
      if (clickedSession.is_selected) {
        // Only update UI state - close any open dropdowns
        this.chatSessionList = this.chatSessionList.map(item => ({
          ...item,
          dropdownVisible: false
        }));
        this.chatSessionListChange.emit([...this.chatSessionList]);
        this.resizeDropdownWidth.emit();
        return; // Don't interrupt ongoing streaming or chat state
      }

      // Check if response is streaming and show confirmation popup
      // Check if any request is in progress (streaming, loading, or processing)
      if (this.IsResponseStreaming || this.isLoadingsession || this.loaderShow || this.isProcessing || this.hasActiveStreamingSubscription) {
        this.pendingSessionSwitch = session;
        this.showSessionSwitchConfirmationPopup = true;
        return;
      }

      // Only proceed with full session switching logic if a different session is selected
      this.executeSessionSwitch(session, true);
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "onSelectSession");
    }
  }

  positionChatMenu(session: any, event: Event): void {
    try {
      event.stopPropagation();
      const index = this.chatSessionList.findIndex((item) => item.session_id === session.session_id);
      if (index === -1) return;
      
      this.chatSessionList.forEach((item: any, i: any) => {
        if (i !== index) item.dropdownVisible = false;
      });
      
      this.chatSessionList[index].dropdownVisible = !this.chatSessionList[index].dropdownVisible;

      if (this.chatSessionList[index].dropdownVisible) {
        // Resolve the actual button (click might be on the inner <i> icon)
        const target = event.target as HTMLElement;
        const menuButton = (target?.closest?.('.chat-history-icons button') || target) as HTMLElement;
        if (menuButton && typeof menuButton.getBoundingClientRect === 'function') {
          const menuElement = menuButton.closest('.list-view-chatname-input')?.querySelector('.chat-history-menu') as HTMLElement;
          if (menuElement) {
            const boundCloseDropdown = this.closeDropdown.bind(this, index);
            document.addEventListener('click', boundCloseDropdown, { once: true });
            setTimeout(() => this.positionDropdown(menuButton, menuElement), 0);
          }
        } else {
          document.addEventListener('click', this.closeDropdown.bind(this, index), { once: true });
        }
      }
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "positionChatMenu");
    }
  }

  /**
   * Finds the nearest scrollable list container so we can use its visible bottom for above/below positioning.
   * When the list is inside a sidebar (e.g. Conversation History), we use this bottom so the last item
   * opens the menu above when there is no height left below (ChatGPT-style).
   */
  private getScrollContainerBottom(el: HTMLElement | null): number | null {
    if (!el || typeof el.closest !== 'function') return null;
    const scrollParent = el.closest('.scrollable-folder, .scrollable-tab');
    if (!scrollParent || !(scrollParent instanceof HTMLElement)) return null;
    return scrollParent.getBoundingClientRect().bottom;
  }

  private getScrollContainerTop(el: HTMLElement | null): number | null {
    if (!el?.closest) return null;
    const scrollParent = el.closest('.scrollable-tab, .scrollable-folder');
    if (!scrollParent || !(scrollParent instanceof HTMLElement)) return null;
    return scrollParent.getBoundingClientRect().top;
  }

  /**
   * Sets above/below class on the dropdown so it opens above when there is not enough space below.
   * Menu uses position: absolute so it scrolls with the session list (same as session-history).
   */
  private positionDropdown(menuButton: HTMLElement, menuElement: HTMLElement): void {
    try {
      const buttonRect = menuButton.getBoundingClientRect();
      const margin = 10;
      
      const scrollBottom = this.getScrollContainerBottom(menuButton);
      const effectiveViewportBottom = scrollBottom != null
        ? Math.min(window.innerHeight - margin, scrollBottom)
        : window.innerHeight - margin;

      const scrollTop = this.getScrollContainerTop(menuButton);
      const effectiveViewportTop = scrollTop != null
        ? Math.max(margin, scrollTop)
        : margin;

      const estimatedHeight = 132;
      const menuHeight = menuElement.offsetHeight > 0 ? menuElement.offsetHeight : estimatedHeight;
      
      const spaceBelow = effectiveViewportBottom - (buttonRect.bottom + 5);
      const spaceAbove = buttonRect.top - effectiveViewportTop - 5;
      
      const inLowerHalf = buttonRect.bottom > window.innerHeight * 0.5;
      const preferAbove = inLowerHalf && spaceAbove >= menuHeight;
      const useAbove = (preferAbove || spaceBelow < menuHeight) && spaceAbove >= menuHeight;

      if (useAbove) {
        menuElement.classList.remove('chat-history-menu-below');
        menuElement.classList.add('chat-history-menu-above');
      } else {
        menuElement.classList.remove('chat-history-menu-above');
        menuElement.classList.add('chat-history-menu-below');
      }
    } catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), 'positionDropdown');
    }
  }

  closeDropdown(index: number): void {
    try {
      this.chatSessionList[index].dropdownVisible = false;
      document.removeEventListener('click', this.closeDropdown.bind(this, index));
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "closeDropdown");
    }
  }

  confirmDelete(session: any, event: Event): void {
    try {
      event.stopPropagation();
      const index = this.chatSessionList.findIndex((item) => item.session_id === session.session_id);
      if (index === -1) return;
      
      this.chatSessionList.forEach((item: any) => {
        item.isDeleting = false;
        item.is_edited = false;
        item.dropdownVisible = false;
      });
      
      this.selectedItemForAction = { ...this.chatSessionList[index], isDeleting: true };
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "confirmDelete");
    }
  }

  cancelDelete(session: any): void {
    try {
      if (!this.selectedItemForAction) return;
      
      this.selectedItemForAction = null;
      this.isResponseScroll = false;
      this.universalLoading.emit(false);
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "cancelDelete");
    }
  }

  deleteSession(session: any): void {
    try {
      if (!this.selectedItemForAction) return;
      
      const index = this.chatSessionList.findIndex((item) => item.session_id === this.selectedItemForAction.session_id);
      if (index === -1) return;

      this.resetChatState.emit();
      this.isResponseScroll = true;

      this.universalLoading.emit(true);
      let sessionid = this.selectedItemForAction.session_id;

      this.chatbotService.DeleteChatSession(sessionid, this.UserID, this.tenantID, this.DomainID).subscribe({
        next: (response: HttpResponse<any>) => {
          this.universalLoading.emit(false);
          this.selectedItemForAction = null; // Clear popup state
          if (response.status === 200) {
            if (response.body && response.body.status === "Success" && response.body.data) {
              this.chatSessionList = this.chatSessionList.filter(item => item.session_id !== sessionid); // Update the list
              this.showDeleteSuccessMessage.emit();
              this.chatSessionListChange.emit([...this.chatSessionList]);
              this.ClickNewChat.emit(true);
            }
          } else {
            const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: "DeleteChatSession",
              errorMessage: translatedApologyMsg,
              error: ""
            });
          }
        },
        error: () => {
          this.universalLoading.emit(false);
          this.selectedItemForAction = null; // Clear popup state
          const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: "DeleteChatSession",
              errorMessage: translatedApologyMsg,
              error: ""
            });
        }
      });
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "deleteSession");
      this.universalLoading.emit(false);
      this.selectedItemForAction = null;
    }
  }

  renameSession(session: any, event: Event): void {
    try {
      event.stopPropagation();
      const index = this.chatSessionList.findIndex((item) => item.session_id === session.session_id);
      if (index === -1) return;
      
      this.chatSessionList.forEach((item: any) => {
        item.isDeleting = false;
        item.is_edited = false;
        item.dropdownVisible = false;
      });
      
      const chatName = this.chatSessionList[index].chat_name;
      this.selectedItemForAction = { 
        ...this.chatSessionList[index], 
        is_edited: true,
        original_name: chatName,
        chat_name: chatName && chatName.length > 100 ? chatName.substring(0, 100) : chatName
      };
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "renameSession");
    }
  }

  cancelRename(session: any): void {
    try {
      if (!this.selectedItemForAction) return;
      
      if (this.selectedItemForAction.original_name) {
        const index = this.chatSessionList.findIndex((item) => item.session_id === this.selectedItemForAction.session_id);
        if (index !== -1) {
          this.chatSessionList[index].chat_name = this.selectedItemForAction.original_name;
        }
      }
      
      this.selectedItemForAction = null;
      this.isResponseScroll = false;
      this.universalLoading.emit(false);
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "cancelRename");
    }
  }

  updateSession(session: any): void {
    try {
      if (!this.selectedItemForAction || this.selectedItemForAction.chat_name === '') return;
      if (this.selectedItemForAction.chat_name && this.selectedItemForAction.chat_name.length > 100) return;
      
      
      const index = this.chatSessionList.findIndex((item) => item.session_id === this.selectedItemForAction.session_id);
      if (index === -1) return;

      this.resetChatState.emit();
      this.universalLoading.emit(true);

      const originalName = this.selectedItemForAction.original_name;
      const newChatName = this.selectedItemForAction.chat_name.trim();
      const sessionId = this.selectedItemForAction.session_id;

      
      this.chatbotService.UpdateSessionName(sessionId, newChatName, this.UserID, this.tenantID, this.DomainID).subscribe({
        next: (response: HttpResponse<any>) => {
          this.universalLoading.emit(false);
          this.selectedItemForAction = null; // Clear popup state
          if (response.status !== 200) {
            this.chatSessionList[index].chat_name = originalName;
            const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
            this.showPopupMessage.emit({
              functionname: "UpdateChatSession",
              errorMessage: translatedApologyMsg,
              error: ""
            });
          } else if (newChatName !== originalName) {
            this.chatSessionList[index].chat_name = newChatName;
            this.showEditSuccessMessage.emit();
          }
          this.chatSessionListChange.emit([...this.chatSessionList]);
          this.onSelectSession(this.chatSessionList[index]);
        },
        error: () => {
          this.universalLoading.emit(false);
          this.selectedItemForAction = null; // Clear popup state
          this.chatSessionList[index].chat_name = originalName;
          const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
          this.showPopupMessage.emit({
            functionname: "UpdateChatSession",
            errorMessage: translatedApologyMsg,
            error: ""
          });
        }
      });
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "updateSession");
      this.universalLoading.emit(false);
      this.selectedItemForAction = null;
    }
  }

  getConversationHistoryCount(): number {
    try {
      // Use the selected tab from parent or fall back to local selectedTab
      const currentTab = this.selectedTabInput || this.selectedTab;
      
      // Return the appropriate count based on selected tab
      if (currentTab === 'All') {
        // Sum all module counts for "All" tab
        return Object.values(this.moduleThreadsCounts).reduce((sum, count) => sum + count, 0);
      } else {
        // Return module-specific count for individual tabs
        return this.moduleThreadsCounts[currentTab.toLowerCase()] || 0;
      }
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "getConversationHistoryCount");
      return 0;
    }
  }

  /**
   * Check if a specific dataset is currently loading
   */
  isDatasetLoading(datasetName: string): boolean {
    return this.datasetLoadingStates[datasetName] || false;
  }

  /**
   * Check if a specific dataset API has been triggered
   */
  isDatasetApiTriggered(datasetName: string): boolean {
    return this.datasetApiTriggered[datasetName.toLowerCase()] || false;
  }

  // Helper to check if dataset is doing initial load (no existing sessions)
  isDatasetInitialLoading(datasetName: string): boolean {
    if (!this.isDatasetLoading(datasetName)) {
      return false;
    }
    // It's initial loading if loading AND no existing sessions for this dataset
    return this.getGroupedSessions()[datasetName]?.length === 0;
  }

  // Helper to check if dataset is doing lazy load (has existing sessions and is loading more)
  isDatasetLazyLoading(datasetName: string): boolean {
    if (!this.isDatasetLoading(datasetName)) {
      return false;
    }
    // It's lazy loading if loading AND has existing sessions for this dataset
    return this.getGroupedSessions()[datasetName]?.length > 0;
  }

  /**
   * Check if any dataset is currently loading
   */
  isAnyDatasetLoading(): boolean {
    return Object.values(this.datasetLoadingStates).some(loading => loading) || this.isLoadingsession;
  }

  selectTab(tabName: string): void {
    try {
      this.selectedTab = tabName;
      this.updateScrollSubscriptions();
      
      // Emit tab selection event for any tab selection
      this.tabSelected.emit({ tabName });
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "selectTab");
    }
  }

  toggleDataset(datasetName: string): void {
    try {
      if (this.expandedDatasets.hasOwnProperty(datasetName)) {
        const wasExpanded = this.expandedDatasets[datasetName];
        this.expandedDatasets[datasetName] = !this.expandedDatasets[datasetName];
        this.updateScrollSubscriptions();
        
        // Emit the toggle event so parent component can load sessions for this dataset
        this.datasetToggled.emit({
          datasetName: datasetName,
          isExpanded: this.expandedDatasets[datasetName]
        });
      }
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "toggleDataset");
    }
  }

  getGroupedSessions(): { [key: string]: any[] } {
    try {
      const grouped: { [key: string]: any[] } = {};
      this.vectorDbDatasets.forEach(dataset => {
        grouped[dataset.name] = this.chatSessionList.filter(
          item => item.module_name?.toLowerCase() === dataset.name?.toLowerCase() && item.chat_name !== 'New Topic'
        );
      });
      return grouped;
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "getGroupedSessions");
      return {};
    }
  }

  getFilteredSessions(): any[] {
    try {
      return this.chatSessionList.filter(
        item => item.module_name?.toLowerCase() === this.selectedTab?.toLowerCase() && item.chat_name !== 'New Topic'
      );
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "getFilteredSessions");
      return [];
    }
  }

  hasNoDataForSelectedTab(): boolean {
    try {
      if (this.selectedTab === 'All') return false;
      return this.getFilteredSessions().length === 0;
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "hasNoDataForSelectedTab");
      return true;
    }
  }

  NewTopic(event: boolean): void {
    try {
      this.ClickNewChat.emit(event);
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "NewTopic");
    }
  }

  toggleFilterDropdown(): void {
    try {
      this.showFilterDropdown = !this.showFilterDropdown;
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "toggleFilterDropdown");
    }
  }

  setFilter(filter: string): void {
    try {
      // Check if the same filter is already selected - no need to do anything
      if (this.selectedFilter === filter) {
        this.showFilterDropdown = false;
        return;
      }

      // Check if response is streaming and show confirmation popup
      if (this.IsResponseStreaming || this.isLoadingsession || this.loaderShow || this.isProcessing || this.hasActiveStreamingSubscription) {
        this.pendingFilterSwitch = filter;
        this.showFilterSwitchConfirmationPopup = true;
        this.showFilterDropdown = false;
        return;
      }

      // Proceed with normal filter change
      this.selectedFilter = filter;
      this.updateFilter.emit(filter);
      this.showFilterDropdown = false;
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "setFilter");
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    try {
      if (this.showFilterDropdown && this.dropdownElement && 
          !this.dropdownElement.nativeElement.contains(event.target) &&
          !((event.target as HTMLElement).closest('.days-dropdown'))) {
        this.showFilterDropdown = false;
      }
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "onDocumentClick");
    }
  }

  shareSession(item: any, event: Event): void {
    try {
      event.stopPropagation();
      if (!this.tenantID || !this.DomainID) return;
      
      const index = this.chatSessionList.findIndex((session) => session.session_id === item.session_id);
      if (index === -1) return;
      
      this.chatSessionList.forEach((session: any) => {
        session.isDeleting = false;
        session.is_edited = false;
        session.is_shared = false;
        session.dropdownVisible = false;
      });
      
      this.universalLoading.emit(true);
      
      this.chatbotService.sharedChat('share_link', this.tenantID, item.session_id).subscribe({
        next: (response: HttpResponse<any>) => {
          this.universalLoading.emit(false);
          if (response.body && response.body.status === 'Success') {
            const shareLink = `${window.location.origin}/shared-chat/${response.body.share_link}?tenant_id=${this.tenantID}&thread_id=${item.session_id}`;
            this.selectedItemForAction = { 
              ...this.chatSessionList[index], 
              is_shared: true,
              share_link: shareLink
            };
          } else {
            this.showPopupMessage.emit({
              functionname: "ShareSession",
              errorMessage: "Failed to generate share link",
              error: ""
            });
          }
        },
        error: () => {
          this.universalLoading.emit(false);
          const translatedApologyMsg = this.translate.instant('APOLOGY_MESSAGE_DEFAULT');
          this.showPopupMessage.emit({
            functionname: "ShareSession",
            errorMessage: translatedApologyMsg,
            error: ""
          });
        }
      });
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "shareSession");
      this.universalLoading.emit(false);
    }
  }

  public copyToClipboard(text: string): void {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
        }).catch(() => {
          this.fallbackCopyToClipboard(text);
        });
      } else {
        this.fallbackCopyToClipboard(text);
      }
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "copyToClipboard");
      this.fallbackCopyToClipboard(text);
    }
  }

  public fallbackCopyToClipboard(text: string): void {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
      } catch (err) {
        this.loggerService.logError(`Failed to copy to clipboard: ${err}`, "fallbackCopyToClipboard");
        this.showPopupMessage.emit({
          functionname: "ShareSession",
          errorMessage: this.translate.instant('FAILED_TO_COPY_SHARED_LINK_ERR_MSG'),
          error: ""
        });
      } finally {
        document.body.removeChild(textArea);
      }
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "fallbackCopyToClipboard");
    }
  }

  copyShareLink(session: any): void {
    try {
      if (!this.selectedItemForAction || !this.selectedItemForAction.share_link) return;
      
      this.copyToClipboard(this.selectedItemForAction.share_link);
      this.showShareSuccessMessage.emit();
      this.selectedItemForAction = null;
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "copyShareLink");
    }
  }

  cancelShare(session: any): void {
    try {
      if (!this.selectedItemForAction) return;
      
      this.selectedItemForAction = null;
      this.isResponseScroll = false;
      this.universalLoading.emit(false);
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "cancelShare");
    }
  }

  confirmSwitchSession(): void {
    try {
      // Hide the popup first
      this.showSessionSwitchConfirmationPopup = false;
      
      if (!this.pendingSessionSwitch) return;
      
      // Re-check current processing state (request might have completed while popup was open)
      const isCurrentlyProcessing = this.IsResponseStreaming || this.isLoadingsession || this.loaderShow || this.isProcessing || this.hasActiveStreamingSubscription;
      
      if (!isCurrentlyProcessing) {
        // Continue with session switching (don't remove current session since response completed)
        const session = this.pendingSessionSwitch;
        this.proceedWithSessionSwitch(session);
        this.pendingSessionSwitch = null;
        return;
      }
      
      // Response is still streaming - proceed with original logic
      // Check if current session has 1 or fewer questions and remove it
      if (this.ChatQueriesList.length <= 1 && this.currentSessionId && this.currentSessionId.length > 0) {
        const currentSession = this.currentSessionId[0];
        if (currentSession && currentSession.session_id) {
          // Remove the current session from the session list
          this.chatSessionList = this.chatSessionList.filter((item: any) => 
            item.session_id !== currentSession.session_id
          );
        }
      }
      
      // Proceed with session switching - call the optimized session switch logic
      const session = this.pendingSessionSwitch;
      this.executeSessionSwitch(session, true);
      
      // Reset the pending state
      this.pendingSessionSwitch = null;
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "confirmSwitchSession");
    }
  }

  cancelSwitchSession(): void {
    try {
      // Simply hide the popup and reset state
      this.showSessionSwitchConfirmationPopup = false;
      this.pendingSessionSwitch = null;
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "cancelSwitchSession");
    }
  }

  confirmFilterSwitchWhileStreaming(): void {
    try {
      // Hide the popup first
      this.showFilterSwitchConfirmationPopup = false;
      
      if (!this.pendingFilterSwitch) return;
      
      // Re-check current processing state (request might have completed while popup was open)
      const isCurrentlyProcessing = this.IsResponseStreaming || this.isLoadingsession || this.loaderShow || this.isProcessing || this.hasActiveStreamingSubscription;
      
      if (!isCurrentlyProcessing) {
        // Response completed while popup was open
        this.loggerService.logError("Response completed while popup was open", "confirmFilterSwitchWhileStreaming");
        // Continue with normal filter change
        this.selectedFilter = this.pendingFilterSwitch;
        this.updateFilter.emit(this.pendingFilterSwitch);
        this.pendingFilterSwitch = '';
        return;
      }
      
      // Response is still streaming - proceed with stopping and changing filter
      // Stop the streaming
      this.stopStreamingFunc.emit();
      
      // Apply the filter change
      this.selectedFilter = this.pendingFilterSwitch;
      this.updateFilter.emit(this.pendingFilterSwitch);
      
      // Reset the pending state
      this.pendingFilterSwitch = '';
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "confirmFilterSwitchWhileStreaming");
    }
  }

  cancelFilterSwitchWhileStreaming(): void {
    try {
      // Simply hide the popup and reset state
      this.showFilterSwitchConfirmationPopup = false;
      this.pendingFilterSwitch = '';
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "cancelFilterSwitchWhileStreaming");
    }
  }

  private closeWebSocketsIfOpen(): void {
    try {
      // Close WebSocket connection only if it's currently open
      if (this.chatbotService.AllWebSocketsConnectionsOpen() || this.chatbotService.isWebSocketOpen() || this.chatbotService.isNewsWebSocketOpen() || this.chatbotService.isFileStatusTrackerWebSocketOpen()) {
        this.chatbotService.closeAllWebSockets();
      }
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "closeWebSocketsIfOpen");
    }
  }

  private executeSessionSwitch(session: any, shouldStopStreaming: boolean = false): void {
    try {
      // Stop streaming functions if needed
      if (shouldStopStreaming) {
        this.stopStreamingFunc.emit();
        this.stopSpeakingFunc.emit();
      }
      
      // Resize dropdown
      this.resizeDropdownWidth.emit();
      
      // Close WebSockets
      this.closeWebSocketsIfOpen();

      // Reset chat state
      this.resetChatState.emit();
      if (this.txtChat && this.autoResizeDirective) {
        this.autoResizeDirective.resetHeight();
      }

      // Update session list
      this.chatSessionList = this.chatSessionList.map(item => ({
        ...item,
        dropdownVisible: false,
        is_available: true,
        is_edited: false,
        is_selected: item.session_id === session.session_id
      }));
      this.chatSessionListChange.emit([...this.chatSessionList]);

      // Set like selected and get chat history
      // Note: GetChatHistory will handle its own loading state management
      this.IsLikeSelected = true;
      const sessionIds = session.session_id.split(',');
      this.getChatHistory.emit(sessionIds);
      
      // Emit scroll to bottom event after session selection
      setTimeout(() => {
        this.scrollToBottomAfterSession.emit();
      }, 900);
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "executeSessionSwitch");
    }
  }

  private proceedWithSessionSwitch(session: any): void {
    try {
      // This method handles session switching when no streaming interruption is needed
      this.executeSessionSwitch(session, false);
    }
    catch (error: any) {
      this.loggerService.logError(this.ChatSessionsError(error), "proceedWithSessionSwitch");
    }
  }

  private async loadAvailableAgents(): Promise<void> {
    try {
      // ✅ NEW: Check config.json flag first - skip loading if disabled
      const enableAgentsInV2 = this.configurationService.isAgentsEnabledInV2();
      if (!enableAgentsInV2) {
        console.log('[ChatSessions] Agents disabled in v2.0 via config.json flag');
        this.availableAgents = [];
        return;
      }
      
      // ✅ Check if agents feature is available
      if (!this.agentFeatureService.isAvailable()) {
        this.availableAgents = [];
        return;
      }
      
      // Pass privilegesData to avoid redundant API call
      this.availableAgents = await this.agentAccessControlService.getAvailableAgents(
        undefined, 
        0, 
        0, 
        this.privilegesData
      );
      
      console.log('[ChatSessions] Loaded agents for v2.0:', this.availableAgents.length);
  }
  catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), 'loadAvailableAgents');
    this.availableAgents = [];
  }
}

handleAgentSelection(agent: AgentConfig): void {
  try {
    this.currentAgentId = agent.id;
    
    // ✅ Emit just the ID (string type - no dependency on AgentConfig interface)
    this.agentSelectedFromSidebar.emit(agent.id);
  }
  catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), "handleAgentSelection");
  }
}

/**
 * Get current agent session ID
 */
getCurrentAgentSessionId(): string {
  try {
    // Get current thread ID from agent orchestrator
    return this.agentOrchestrator?.getCurrentThreadId() || '';
  } catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), 'getCurrentAgentSessionId');
    return '';
  }
}

/**
 * Handle agent session selection
 * Loads chat history for the selected agent thread
 * @param thread - The selected thread object containing thread_id
 */
onAgentSessionSelected(thread: any): void {
  try {
    if (!thread.thread_id) {
      this.loggerService.logError('[ChatSessions] No thread_id provided', 'onAgentSessionSelected');
      return;
    }

    // Show session loading overlay (same as chatbot)
    this.universalLoading.emit(true);

    // Set current thread ID for history tracking
    this.currentThreadIdForHistory = thread.thread_id;
    
    // Set thread in agent orchestrator
    if (this.agentOrchestrator) {
      this.agentOrchestrator.setCurrentThreadId(thread.thread_id);
      this.agentOrchestrator.setIsNewChat(false);
    }

    // Reset pagination state for new thread
    this.historyCurrentPage = 1;
    this.historyTotalPages = 1;
    
    // Load initial history (page 1)
    this.loadAgentChatHistory(thread.thread_id, 1, true);
  }
  catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), "onAgentSessionSelected");
    this.universalLoading.emit(false);
  }
}

/**
 * Load agent chat history for a specific thread
 * Supports pagination for lazy loading
 * @param threadId - The thread ID to load history for
 * @param page - The page number to load
 * @param isInitialLoad - Whether this is the first page load
 */
private loadAgentChatHistory(threadId: string, page: number, isInitialLoad: boolean): void {
  try {
    // Prevent duplicate requests
    if (this.isLoadingMoreHistory) {
      return;
    }

    // Check if we've loaded all pages
    if (!isInitialLoad && this.historyCurrentPage >= this.historyTotalPages) {
      return;
    }

    // Set loading state
    this.isLoadingMoreHistory = true;
    if (this.agentOrchestrator) {
      const hasMore = (page < this.historyTotalPages) || isInitialLoad;
      this.agentOrchestrator.updateLazyLoadState(true, hasMore);
    }

    // Build request
    const request: GetAgentResponseHistoryRequest = {
      thread_id: threadId,
      user_id: this.UserID || '',
      tenant_id: this.tenantID || '',
      page: page,
      page_size: 5 // Load 5 messages per page
    };

    // Make API call, passing current agent ID for agent-specific API routing
    this.getAgentResponseHistoryService.getResponseHistory(request, this.currentAgentId)
      .subscribe({
        next: (response) => {
          
          if (response.status === 'success' && response.data) {
            // Update pagination state
            this.historyCurrentPage = response.data.pagination.current_page;
            this.historyTotalPages = response.data.pagination.total_pages;

            // Sync pagination to orchestrator for cross-component consistency
            if (this.agentOrchestrator) {
              this.agentOrchestrator.updateHistoryPagination(this.historyCurrentPage, this.historyTotalPages);
            }

            // Convert API responses to ChatQuery format
            const historyMessages = response.data.responses.map((resp: any) => ({
              question: resp.question,
              answer: resp.answer.text,
              response_id: resp.response_id,
              updated_at: resp.updated_at,
              isHistory: true,
              pills: resp.source_metadata?.follow_up_questions || [],
              followUpQuestions: resp.source_metadata?.follow_up_questions || [],
              metadata: resp.source_metadata?.references || resp.metadata,
              retrieval_mode: normalizeRetrievalMode(resp.answer?.retrieval_mode),
              is_like: resp.is_like,
              feedback: resp.feedback,
              comments: resp.comments
            })).reverse();


            // Check if thread has no history - show agent landing page
            if (isInitialLoad && historyMessages.length === 0) {
              // Empty history - show new chat landing page
              this.ChatQueriesList = [];
              
              if (this.agentOrchestrator) {
                this.agentOrchestrator.setIsNewChat(true);
                this.agentOrchestrator['showAgentLanding'].next(true);
                this.agentOrchestrator['currentThreadId'] = threadId;
              }
              
              this.loggerService.logInfo(
                `[ChatSessions] Thread ${threadId} has no history, showing new chat landing page`,
                'loadAgentChatHistory'
              );
            } else if (isInitialLoad) {
              // Initial load: replace ChatQueriesList
              this.ChatQueriesList = [...historyMessages];
              
              // Update agent orchestrator
              if (this.agentOrchestrator) {
                this.agentOrchestrator.updateChatHistory(this.ChatQueriesList);
              }
            } else {
              // Lazy load: prepend older messages
              this.ChatQueriesList = [...historyMessages, ...this.ChatQueriesList];
              
              // Update agent orchestrator
              if (this.agentOrchestrator) {
                this.agentOrchestrator.updateChatHistory(this.ChatQueriesList);
              }
            }

            // Update lazy load state (only if we have messages)
            if (historyMessages.length > 0) {
              const hasMore = this.historyCurrentPage < this.historyTotalPages;
              if (this.agentOrchestrator) {
                this.agentOrchestrator.updateLazyLoadState(false, hasMore);
                this.agentOrchestrator['currentThreadId'] = threadId;
                this.agentOrchestrator.hideAgentLanding();
              }
            }

            // Hide session loading overlay for initial load (same as chatbot)
            if (isInitialLoad) {
              this.universalLoading.emit(false);
            }
          } else {
            // Error response
            this.loggerService.logError(
              `[ChatSessions] Failed to load history: ${response.message}`,
              'loadAgentChatHistory'
            );
            // Hide session loading overlay on error
            if (isInitialLoad) {
              this.universalLoading.emit(false);
            }
          }
        },
        error: (error) => {
          this.loggerService.logError(
            this.ChatSessionsError(error),
            'loadAgentChatHistory'
          );
          // Hide session loading overlay on error
          if (isInitialLoad) {
            this.universalLoading.emit(false);
          }
        },
        complete: () => {
          // Reset loading state
          this.isLoadingMoreHistory = false;
          if (this.agentOrchestrator) {
            const hasMore = this.historyCurrentPage < this.historyTotalPages;
            this.agentOrchestrator.updateLazyLoadState(false, hasMore);
          }
        }
      });
  }
  catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), 'loadAgentChatHistory');
    this.isLoadingMoreHistory = false;
    if (this.agentOrchestrator) {
      this.agentOrchestrator.updateLazyLoadState(false, false);
    }
    // Hide session loading overlay on error
    this.universalLoading.emit(false);
  }
}

/**
 * Load more agent chat history (for lazy loading)
 * Called when user scrolls to top of chat
 * @returns boolean - true if loading initiated, false otherwise
 */
onLoadMoreAgentChatHistory(): boolean {
  try {
    // Check if we're in agent mode with an active thread
    if (!this.currentThreadIdForHistory || this.currentAgentId === 'module') {
      return false;
    }

    // Check if already loading or all pages loaded
    if (this.isLoadingMoreHistory) {
      return false;
    }

    // IMPORTANT: Check if we've loaded all available pages
    if (this.historyCurrentPage >= this.historyTotalPages) {
      // All pages loaded, update state to prevent further attempts
      if (this.agentOrchestrator) {
        this.agentOrchestrator.updateLazyLoadState(false, false);
      }
      return false;
    }

    // Calculate next page
    const nextPage = this.historyCurrentPage + 1;

    // Safety check: Don't load beyond total pages
    if (nextPage > this.historyTotalPages) {
      // Update lazy load state to indicate no more pages
      if (this.agentOrchestrator) {
        this.agentOrchestrator.updateLazyLoadState(false, false);
      }
      return false;
    }
    
    this.loadAgentChatHistory(this.currentThreadIdForHistory, nextPage, false);
    return true;
  }
  catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), 'onLoadMoreAgentChatHistory');
    return false;
  }
}

/**
 * Handle agent new chat request
 */
onAgentNewChat(): void {
  try {
    // Emit new chat event
    this.ClickNewChat.emit(true);
  }
  catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), "onAgentNewChat");
  }
}

/**
 * Add a new agent session to the history panel
 * This is called when a user asks a question and no session exists
 * @param threadName - The name of the session (usually the question)
 * @param threadId - Optional thread ID
 */
addAgentSession(threadName: string, threadId?: string): void {
  try {
    
    if (this.sessionHistoryComponent) {
      this.sessionHistoryComponent.addNewThread(threadName, threadId);
    }
  }
  catch (error: any) {
    this.loggerService.logError(this.ChatSessionsError(error), "addAgentSession");
  }
}
}